import { Alert, Box, Chip, Grid, Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ApprovalType, ConfirmationDialogResult, DatabaseRecordType, EnumHelper, RiskAssesmentFrequency, RiskTreatmentState } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as store from "source/framework/customStore";
import * as storeActions from "source/models/store/storeActions";
import * as riskService from "source/models/services/riskService";
import { ISessionState } from "source/models/session/sessionState";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { IModifyUser, ModifyUser, SaveModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { optionCodeRiskMatrixInfoDocument, removeConfirmationWord, residualRiskLevelThreshold } from "ui/Scripts/source/models/consts";
import { Base } from "ui/Scripts/source/framework/base";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { SaveDataObj } from "source/framework/saveData";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { FormText } from "source/components/framework/FormText";
import { DatePicker } from "source/components/framework/DateTime/DatePicker";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard/DialogSectionCard";
import { RiskListItem } from "ui/Scripts/source/models/risk/riskListItem";
import { RiskListLevelChip } from "../RiskList/RiskListLevelChip";
import { RiskListTraceabilityChip } from "../RiskList/RiskListTraceabilityChip";
import { UserBlocks } from "source/components/user/UserBlocks/UserBlocks";
import { getNextAssessmentDate, IRiskAssessmentEditItem } from "ui/Scripts/source/models/riskAssessment/riskAssessmentEditItem";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { UserListItems } from "source/models/user/userListItems";
import { RiskListApprovalChip } from "../RiskList/RiskListApprovalChip";
import { AppTrendingIcon } from "source/components/framework/Icon/AppTrendingIcon";

const valueNotSelected = -1;

interface IRiskAssessmentEditDialogProp {
    sessionState: ISessionState;
    companyId: string;
    riskId: string;
    unitId: string;
    riskAssessmentId: string;
    assesmentFrequency: RiskAssesmentFrequency;
    code: string;
    title: string;
    description: string;
    threat: string;
    controls: string;
    isReadOnly: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onSaved: (id: string) => void;
    onClose: () => void;
    onPrint: () => void;
    onRefreshList: () => Promise<void>;
    onPrevious: () => void;
    onNext: () => void;
}

interface IRiskAssessmentEditDialogState {
    isLoading: boolean;
    isNew: boolean;
    riskAssessment: IRiskAssessmentEditItem;
    previousRiskLevel: number;
    previousResidualRiskLevel: number;
    id: string;
    rowId: string;
    dateStr: string;
    assessor: string;
    nextAssessmentDateStr: string;
    likelihood: number;
    impact: number;
    riskLevel: number;
    controlEffiency: number;
    comment: string;
    treatments: string;
    treatmentTargetDateStr: string;
    treatmentEfficiency: number;
    residualRiskLevel: number;
    approval: ApprovalType | typeof valueNotSelected;
    approvalDateStr: string;
    approvalAuthority: string;
    approvalJustification: string;
    personsInCharge: IModifyUser[];
    canEdit: boolean;
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
}

const likelihoodOptions = [
    { id: valueNotSelected.toString(), title: `'${Translations.Select}'` },
    { id: "1", title: RiskListItem.getRiskLikelihoodText(1) },
    { id: "2", title: RiskListItem.getRiskLikelihoodText(2) },
    { id: "3", title: RiskListItem.getRiskLikelihoodText(3) },
    { id: "4", title: RiskListItem.getRiskLikelihoodText(4) },
    { id: "5", title: RiskListItem.getRiskLikelihoodText(5) }
];

const impactOptions = [
    { id: valueNotSelected.toString(), title: `'${Translations.Select}'` },
    { id: "1", title: RiskListItem.getRiskImpactText(1) },
    { id: "2", title: RiskListItem.getRiskImpactText(2) },
    { id: "3", title: RiskListItem.getRiskImpactText(3) },
    { id: "4", title: RiskListItem.getRiskImpactText(4) },
    { id: "5", title: RiskListItem.getRiskImpactText(5) }
];

const controlEffiencyOptions = [
    { id: valueNotSelected.toString(), title: `'${Translations.Select}'` },
    { id: "0", title: RiskListItem.getRiskControlEfficiencyText(0) },
    { id: "1", title: RiskListItem.getRiskControlEfficiencyText(1) },
    { id: "2", title: RiskListItem.getRiskControlEfficiencyText(2) },
    { id: "3", title: RiskListItem.getRiskControlEfficiencyText(3) },
];

const treatmentEfficiencyOptions = [
    { id: "0", title: "0 %" },
    { id: "10", title: "10 %" },
    { id: "20", title: "20 %" },
    { id: "30", title: "30 %" },
    { id: "40", title: "40 %" },
    { id: "50", title: "50 %" },
    { id: "60", title: "60 %" },
    { id: "70", title: "70 %" },
    { id: "80", title: "80 %" },
    { id: "90", title: "90 %" }
];

const approvalOptions = [
    { id: valueNotSelected.toString(), title: `'${Translations.Select}'` },
    { id: ApprovalType.NotApproved.toString(), title: EnumHelper.getApprovalTypeText(ApprovalType.NotApproved) },
    { id: ApprovalType.ConditionallyApproved.toString(), title: EnumHelper.getApprovalTypeText(ApprovalType.ConditionallyApproved) },
    { id: ApprovalType.Approved.toString(), title: EnumHelper.getApprovalTypeText(ApprovalType.Approved) },
];

const getProcessColor = (process: number): string => {
    if (process >= 100) return "success.main";
    if (process >= 50) return "warning.main";
    return "error.main";
};

const RiskProcessTitle = (props: { process: number; title: string, containerDiv: React.RefObject<HTMLDivElement>, className: string }) => {
    const scrollToLastOpenedDocument = () => {
        if (props.className && props.containerDiv.current && props.containerDiv.current.scrollTo !== null && props.containerDiv.current.scrollTo !== undefined) {
            const elements = props.containerDiv.current.getElementsByClassName(props.className);
            if (elements.length > 0 && elements[0]) {
                const y = (elements[0] as HTMLDivElement).offsetTop;
                props.containerDiv.current.scrollTo({
                    top: y > 0 ? y - 1 : y,
                    left: 0,
                    behavior: "auto"
                });
            }
        }
    };

    return (<Box display="flex" alignItems="center">
        <Tooltip title={`${props.process}%`}>
            <Chip
                size="small"
                sx={{ backgroundColor: getProcessColor(props.process), width: "24px", height: "24px", marginRight: "8px", cursor: "pointer" }}
                onClick={scrollToLastOpenedDocument}
            />
        </Tooltip>
        <Box>{props.title}</Box>
    </Box>);
};

export const RiskAssessmentEditDialog = (props: IRiskAssessmentEditDialogProp) => {
    const _isMounted = useRef(false);
    const containerDiv = useRef<HTMLDivElement>(null);
    const orgStateHash = useRef("");

    const [state, setState] = useState<IRiskAssessmentEditDialogState>({
        isLoading: false,
        isNew: false,
        riskAssessment: null,
        previousRiskLevel: null,
        previousResidualRiskLevel: null,
        id: "",
        rowId: "",
        dateStr: "",
        assessor: "",
        nextAssessmentDateStr: "",
        likelihood: valueNotSelected,
        impact: valueNotSelected,
        riskLevel: 0,
        controlEffiency: valueNotSelected,
        comment: "",
        treatments: "",
        treatmentTargetDateStr: "",
        treatmentEfficiency: 0,
        residualRiskLevel: 0,
        approval: valueNotSelected,
        approvalDateStr: "",
        approvalAuthority: "",
        approvalJustification: "",
        personsInCharge: [],
        canEdit: false,
        lockedUser: null,
        isLockedToOtherUser: false,
    });

    const loadRiskAssessment = async (id: string) => {
        //Unlock current risk assessment
        const unlockRecord = state.canEdit;
        if (unlockRecord && state.id && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.RiskAssessment);
        }
        //Load and lock risk assessment
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => riskService.getRiskAssessmentEdit(props.companyId, props.riskId, id, true), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) return;
        const riskAssessment = result.item;
        const isNew = riskAssessment.isNew();
        ModifyUser.sortModifyUsers(riskAssessment.personsInCharge, { currentUserCode: props.sessionState.code });
        const stateChange = {
            isNew,
            canEdit: result.canEdit,
            riskAssessment: result.item,
            previousRiskLevel: result.previousRiskLevel,
            previousResidualRiskLevel: result.previousResidualRiskLevel,
            id: riskAssessment.id,
            rowId: riskAssessment.rowId,
            dateStr: Base.utcTimeToDateStr(riskAssessment.date),
            assessor: riskAssessment.assessor,
            nextAssessmentDateStr: Base.utcTimeToDateStr(isNew
                ? getNextAssessmentDate(props.assesmentFrequency, riskAssessment.nextAssessmentDate)
                : riskAssessment.nextAssessmentDate),
            likelihood: isNew
                ? (riskAssessment.likelihood ? riskAssessment.likelihood : valueNotSelected)
                : riskAssessment.likelihood,
            impact: isNew
                ? (riskAssessment.impact ? riskAssessment.impact : valueNotSelected)
                : riskAssessment.impact,
            riskLevel: riskAssessment.riskLevel,
            controlEffiency: isNew
                ? valueNotSelected
                : riskAssessment.controlEffiency,
            comment: riskAssessment.comment,
            treatments: riskAssessment.treatments,
            treatmentTargetDateStr: Base.utcTimeToDateStr(riskAssessment.treatmentTargetDate),
            treatmentEfficiency: riskAssessment.treatmentEfficiency,
            residualRiskLevel: riskAssessment.residualRiskLevel,
            approval: isNew || Base.isNullOrUndefined(riskAssessment.approval)
                ? valueNotSelected
                : riskAssessment.approval,
            approvalDateStr: Base.utcTimeToDateStr(riskAssessment.approvalDate),
            approvalAuthority: riskAssessment.approvalAuthority,
            approvalJustification: riskAssessment.approvalJustification,
            lockedUser: riskAssessment.lockedUser,
            isLockedToOtherUser: riskAssessment.isLockedToOtherUser(props.sessionState.code),
            personsInCharge: riskAssessment.personsInCharge,
        };
        setState(old => {
            const newState = { ...old, ...stateChange };
            orgStateHash.current = getSaveDataFromState(newState).hash;
            return newState;
        });
        //Add locked record to idb
        if (!isNew && state.canEdit && LockedRecord.isLockedToUser(riskAssessment.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(props.companyId, riskAssessment.id, props.sessionState.code, DatabaseRecordType.RiskAssessment));
        }
        //Send read receipt for risk assessment
        if (!isNew && !riskAssessment.isRead) {
            const userIsCreator = riskAssessment.createUser.code === props.sessionState.code;
            if (!userIsCreator) {
                AppUtils.callService(() => riskService.sendRiskAssessmentReadReceipt(props.companyId, riskAssessment.id), false, true);
            }
        }
    };

    const componentWillUnmount = useRef(false);

    useEffect(() => {
        _isMounted.current = true;
        
        return () => {
            _isMounted.current = false;
            componentWillUnmount.current = true;
        };
    }, []);

    useEffect(() => {
        const loadData = async () => {
            if (state.id) {
                const cancelResult = await AppUtils.cancel(getSaveDataFromState(state).hash, orgStateHash.current, null);
                if (cancelResult === ConfirmationDialogResult.Cancel) return;
                if (cancelResult === ConfirmationDialogResult.Yes) {
                    if (!await saveToDb()) return;
                    await props.onRefreshList();
                }
            }
            await loadRiskAssessment(props.riskAssessmentId);
        };

        loadData();
    }, [props.riskAssessmentId]);

    useEffect(() => {
        const unlockRecord = () => {
            if (!state.isNew && state.canEdit && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
                recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.RiskAssessment);
            }
        };

        return () => {
            if (componentWillUnmount.current) {
                unlockRecord();
            }
        };
    }, [state.id, state.isNew, state.canEdit, state.lockedUser]);

    const getRiskAssessmentTitle = (date: number, assessor: string): string => {
        return `${Base.timeToDateStr(date)}: ${assessor}`;
    };

    // #region Locking
    const handleUnlockAndLockToMe = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockRiskAssessment, getRiskAssessmentTitle(state.riskAssessment.date, state.riskAssessment.assessor)), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await recordLockManager.lock(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.RiskAssessment);
        if (!result || result.items.length < 1) return;
        setState((old) => ({
            ...old,
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        }));
    };
    // #endregion Locking

    // #region Saving
    const getSaveDataFromState = (state: IRiskAssessmentEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("riskId", props.riskId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("date", state.dateStr.toUtcDate().getTime());
        result.append("assessor", state.assessor);
        result.append("nextAssessmentDate", state.nextAssessmentDateStr.toUtcDate().getTime());
        result.append("likelihood", state.likelihood);
        result.append("impact", state.impact);
        result.append("riskLevel", state.riskLevel);
        result.append("controlEffiency", state.controlEffiency);
        result.append("comment", state.comment);
        result.append("treatments", state.treatments);
        result.append("treatmentTargetDate", state.treatmentTargetDateStr ? state.treatmentTargetDateStr.toUtcDate().getTime() : null);
        result.append("treatmentEfficiency", state.treatmentEfficiency);
        result.append("residualRiskLevel", state.residualRiskLevel);
        result.append("approval", state.approval < 0 ? null : state.approval);
        result.append("approvalDate", state.approvalDateStr ? state.approvalDateStr.toUtcDate().getTime() : null);
        result.append("approvalAuthority", state.approvalAuthority);
        result.append("approvalJustification", state.approvalJustification);
        result.append("personsInCharge", state.isNew
            ? state.personsInCharge.map(i => new SaveModifyUser(i))
            : null
        );
        return result;
    };

    const checkErrors = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            if (!state.assessor) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.AssessorMustBeDefined));
                return resolve(false);
            }
            if (!state.dateStr) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.AssessmentDateMustBeDefined));
                return resolve(false);
            }
            if (!state.nextAssessmentDateStr) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NextAssessmentDateMustBeDefined));
                return resolve(false);
            }
            if (state.likelihood < 0) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.LikelihoodMustBeDefined));
                return resolve(false);
            }
            if (state.impact < 0) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.ImpactMustBeDefined));
                return resolve(false);
            }
            if (state.controlEffiency < 0) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.ControlEffiencyMustBeDefined));
                return resolve(false);
            }
            if (!state.comment) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.AssessmentCommentMustBeDefined));
                return resolve(false);
            }
            //RiskTreatments
            // if (!state.treatments) {
            //     store.customStore.dispatch(storeActions.showErrorMessage(Translations.AssessmentTreatmentsMustBeDefined));
            //     return resolve(false);
            // }
            // if (state.personsInCharge.length < 1) {
            //     store.customStore.dispatch(storeActions.showErrorMessage(Translations.PersonsInChargeMustBeDefined));
            //     return resolve(false);
            // }
            // if (!state.treatmentTargetDateStr) {
            //     store.customStore.dispatch(storeActions.showErrorMessage(Translations.TreatmentTargetDateMustBeDefined));
            //     return resolve(false);
            // }
            if (state.treatmentTargetDateStr && state.personsInCharge.length < 1) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.RiskAssessmentPersonsInChargeMustBeDefined));
                return resolve(false);
            }
            //Opportunity
            // if (state.opportunity) {
            //     if (!state.opportunityBenefit) {
            //         store.customStore.dispatch(storeActions.showErrorMessage(Translations.OpportunityBenefitMustBeDefined));
            //         return resolve(false);
            //     }
            //     if (!state.opportunityDescription) {
            //         store.customStore.dispatch(storeActions.showErrorMessage(Translations.OpportunityDescriptionMustBeDefined));
            //         return resolve(false);
            //     }
            // }
            //Approval
            if (state.approval > ApprovalType.NotApproved) {
                if (!state.approvalDateStr) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.ApprovalDateMustBeDefined));
                    return resolve(false);
                }
                if (!state.approvalAuthority) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.ApprovalAuthorityMustBeDefined));
                    return resolve(false);
                }
                if (!state.approvalJustification) {
                    store.customStore.dispatch(storeActions.showErrorMessage(Translations.ApprovalJustificationMustBeDefined));
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    const validateBeforeSave = async (): Promise<boolean> => {
        return await AppUtils.validate(checkErrors);
    };

    const saveToDb = async (): Promise<SaveSuccess> => {
        if (!await validateBeforeSave()) return null;
        const saveData = getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() => riskService.saveRiskAssessmentEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        setState((old) => {
            const newState = { ...old, id: result.id, rowId: result.rowId, isNew: false };
            orgStateHash.current = getSaveDataFromState(newState).hash;
            return newState;
        });
        return result;
    };
    // #endregion Saving

    const afterSaveToDb = (id: string) => {
        loadRiskAssessment(id);
        props.onSaved(id);
    };

    const handleSave = async (closeDialog: boolean) => {
        const result = await saveToDb();
        if (!result) return;
        if (!closeDialog) {
            afterSaveToDb(result.id);
            return;
        }
        props.onClose();
    };

    const getRemoveSaveData = (ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", props.riskId);
        result.append("ids", ids);
        return result;
    };

    const handleRemove = async () => {
        if (state.isLockedToOtherUser || state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRiskAssessmentParameter, getRiskAssessmentTitle(state.dateStr.toUtcDate().getTime(), state.assessor)), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => riskService.removeRiskAssessments(getRemoveSaveData([state.id]).obj));
            if (!result) return;
            setState(old => ({ ...old, lockedUser: null }));
            props.onClose();
        }
    };

    // #region Handlers
    const handleAddHandlers = async (users: IModifyUser[]): Promise<ApiSuccess> => {
        if (!users || users.length < 1) return null;
        if (!state.isNew) {
            const result = await AppUtils.callService(() => riskService.riskAssessmentHandlersAdd(props.companyId, props.riskId, state.id, users.map(i => i.code)), true);
            if (!result) return null;
        }
        const personsInCharge = state.personsInCharge.concat(users.map(i => new ModifyUser({ ...i, date: 0 })));
        ModifyUser.sortModifyUsers(personsInCharge, { currentUserCode: props.sessionState.code });
        setState(old => ({ ...old, personsInCharge }));
        props.onSaved(state.id);
        return new ApiSuccess();
    };

    const handleRemoveHandlers = async (userCodes: string[]): Promise<ApiSuccess> => {
        if (!userCodes || userCodes.length < 1) return null;
        if (!state.isNew) {
            const result = await AppUtils.callService(() => riskService.riskAssessmentHandlersRemove(props.companyId, props.riskId, state.id, userCodes));
            if (!result) return null;
        }
        const personsInCharge = state.personsInCharge.filter(i => userCodes.indexOf(i.code) < 0);
        ModifyUser.sortModifyUsers(personsInCharge, { currentUserCode: props.sessionState.code });
        setState(old => ({ ...old, personsInCharge }));
        props.onSaved(state.id);
        return new ApiSuccess();
    };
    
    const handleGetHandlersSelectListItems = (_companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, _ignoreQrCodeUsers: boolean, _requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        return riskService.getRiskAssessmentResponsiblePersonsSelectUserListItems(props.companyId, props.riskId, props.unitId, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected, doNotIgnoreRecordUserConnectedUserCode,
            sortColumn, sortOrderIsAsc);
    };

    const handleApprove = async (userCode: string) => {
        if (!userCode) return;
        const personInCharge = state.personsInCharge.find(i => i.code === userCode);
        if (!personInCharge) return;
        if (!state.isNew) {
            const result = await AppUtils.callService(() => riskService.riskAssessmentHandlersApprove(props.companyId, props.riskId, state.id, [userCode]), true);
            if (!result) return null;
        }
        personInCharge.date = Base.getNowTicks();
        const personsInCharge = ModifyUser.sortModifyUsers(state.personsInCharge.filter(i => i.code !== userCode).concat([new ModifyUser(personInCharge)]), { currentUserCode: props.sessionState.code });
        setState(old => ({ ...old, personsInCharge }));
        props.onSaved(state.id);
    };
    // #endregion Handlers

    const getResidualRiskLevelColor = (riskLevel: number, treatmentEfficiency: number): number => {
        return Math.ceil(riskLevel * (1 - treatmentEfficiency / 100));
    };

    // const handlePrint = async () => {
    //     if (state.isNew) return;
    //     AppUtils.showErrorMessage(Translations.NotImplementedYet);
    //     // props.onPrint();
    // };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel(getSaveDataFromState(state).hash, orgStateHash.current, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave(true);
        } else {
            props.onClose();
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut(getSaveDataFromState(state).hash, orgStateHash.current,
            async () => !!await saveToDb(),
            props.onRefreshList,
            (newStateHash) => { orgStateHash.current = newStateHash; });
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        props.onNext();
    };

    if (!state.riskAssessment) return null;

    const isReadOnly = props.isReadOnly || !state.canEdit;
        
    const canDelete = !state.isNew && !state.isLockedToOtherUser && state.canEdit;

    const nowDateStr = Base.timeToDateStr(Base.getNowTicks());

    const assessmentProcess = Math.ceil(100 * (((state.likelihood < 0 ? 0 : 1) + (state.impact < 0 ? 0 : 1) + (state.controlEffiency < 0 ? 0 : 1) +
        (state.assessor ? 1 : 0) + (state.dateStr ? 1 : 0) + (state.nextAssessmentDateStr ? 1 : 0) + (state.comment ? 1 : 0)) / 7));
    
    const riskTreatmentsProcess = Math.ceil(100 * (((state.treatments ? 1 : 0) + (state.personsInCharge.length > 0 ? 1 : 0) + (state.treatmentTargetDateStr ? 1 : 0)) / 3));

    const approvalProcess = Math.ceil(100 * (((state.approval ? 1 : 0) + (state.approvalDateStr ? 1 : 0) + (state.approvalAuthority ? 1 : 0) + (state.approvalJustification ? 1 : 0)) / 4));

    const treatmentTargetDateNotSpecified = !state.treatmentTargetDateStr;
    const treatmentsNotSpecified = !state.treatments;
    const handlersNotSpecified = state.personsInCharge.length === 0;

    return (
        <Dialog
            id="riskAssessmentEditDialog"
            maxWidth="xl"
            maxHeight="100%"
            title={state.isNew
                ? Translations.AddRiskAssessment
                : `${Translations.EditRiskAssessment}: ${getRiskAssessmentTitle(state.riskAssessment.date, state.riskAssessment.assessor)}${(isReadOnly ? ", " + Translations.ReadOnly : "")}${(state.isLockedToOtherUser ? ", " + Translations.Locked : "")}`
            }
            body={
                <>
                    <Box height="100%">
                        <GridRow height="calc(100% + 30px)">
                            <Grid item sm={3} height="100%">
                                <DialogSectionCard headerTitle={Translations.Risk} height="100%">
                                    <Box sx={{ overflowY: "auto" }}>
                                        <FormField title={Translations.Title} margin={0}>
                                            {props.title}
                                        </FormField>
                                        <FormField title={Translations.Description}>
                                            {props.description}
                                        </FormField>
                                        <FormField title={Translations.RiskThreat}>
                                            {props.threat}
                                        </FormField>
                                        <FormField title={Translations.RiskControls}>
                                            {props.controls}
                                        </FormField>
                                        <FormField title={Translations.RiskAssesmentFrequency}>
                                            {EnumHelper.RiskAssesmentFrequencyToStr(props.assesmentFrequency)}
                                        </FormField>
                                    </Box>
                                </DialogSectionCard>
                            </Grid>
                            <Grid item sm={6} height="100%">
                                <Box height="100%" sx={{ overflowY: "auto", position: "relative" }} ref={containerDiv}>
                                    <DialogSectionCard headerTitle={Translations.Assessment} height="auto" className="assessmentSection">
                                        <GridRow>
                                            <Grid item sm={6}>
                                                <FormField title={Translations.Assessor} fullWidth margin="0">
                                                    <FormText
                                                        required={true}
                                                        disabled={isReadOnly}
                                                        tooltip={Translations.Assessor}
                                                        value={state.assessor}
                                                        maxLength={100}
                                                        onChange={(assessor) => setState((old) => ({ ...old, assessor }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.Date} fullWidth margin="0">
                                                    <DatePicker
                                                        disabled={isReadOnly}
                                                        value={state.dateStr}
                                                        onChange={(dateStr: string) => setState((old) => ({ ...old, dateStr }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.NextAssessmentDate} fullWidth margin="0">
                                                    <DatePicker
                                                        disabled={isReadOnly}
                                                        value={state.nextAssessmentDateStr}
                                                        minDate={state.dateStr}
                                                        onChange={(nextAssessmentDateStr: string) => setState((old) => ({ ...old, nextAssessmentDateStr }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={4}>
                                                <FormField title={Translations.Likelihood}>
                                                    <IdTitleDropdown
                                                        disabled={isReadOnly}
                                                        selectedId={state.likelihood.toString()}
                                                        items={likelihoodOptions}
                                                        onChange={(value: string) => setState(old => {
                                                            const likelihood = value.toInteger(true, valueNotSelected);
                                                            if (likelihood < 0 || old.impact < 0 || old.treatmentEfficiency < 0) {
                                                                return { ...old, likelihood };
                                                            }
                                                            const riskLevel = likelihood * old.impact;
                                                            return {
                                                                ...old,
                                                                likelihood,
                                                                riskLevel,
                                                                residualRiskLevel: getResidualRiskLevelColor(riskLevel, old.treatmentEfficiency)
                                                            };
                                                        })}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={4}>
                                                <FormField title={Translations.Impact}>
                                                    <IdTitleDropdown
                                                        disabled={isReadOnly}
                                                        selectedId={state.impact.toString()}
                                                        items={impactOptions}
                                                        onChange={(value: string) => setState(old => {
                                                            const impact = value.toInteger(true, valueNotSelected);
                                                            if (impact < 0 || old.likelihood < 0 || old.treatmentEfficiency < 0) {
                                                                return { ...old, impact };
                                                            }
                                                            const riskLevel = old.likelihood * impact;
                                                            return {
                                                                ...old,
                                                                impact,
                                                                riskLevel,
                                                                residualRiskLevel: getResidualRiskLevelColor(riskLevel, old.treatmentEfficiency)
                                                            };
                                                        })}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.RiskLevel} fullWidth>
                                                    <RiskListLevelChip riskLevel={state.riskLevel} fullWidth />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={4}>
                                                <FormField title={Translations.ControlEffiency}>
                                                    <IdTitleDropdown
                                                        disabled={isReadOnly}
                                                        selectedId={state.controlEffiency.toString()}
                                                        items={controlEffiencyOptions}
                                                        onChange={(value: string) => setState((old) => ({ ...old, controlEffiency: value.toInteger(true, valueNotSelected) }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.AssessmentComment} fullWidth>
                                                    <FormText
                                                        disabled={isReadOnly}
                                                        maxLength={1000}
                                                        placeholder={Translations.AssessmentCommentPlaceholder}
                                                        tooltip={Translations.AssessmentComment}
                                                        value={state.comment}
                                                        multiline
                                                        rows={6}
                                                        onChange={(comment) => setState((old) => ({ ...old, comment }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    </DialogSectionCard>
                                    <DialogSectionCard headerTitle={Translations.RiskTreatments} height="auto" margin="15px 0 0 0" className="riskTreatmentsSection">
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.RiskTreatments} fullWidth margin="0">
                                                    <FormText
                                                        disabled={isReadOnly}
                                                        maxLength={1000}
                                                        placeholder={Translations.RiskTreatmentsPlaceholder}
                                                        tooltip={Translations.RiskTreatments}
                                                        value={state.treatments}
                                                        multiline
                                                        rows={6}
                                                        onChange={(treatments) => setState((old) => ({ ...old, treatments }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={4}>
                                                <FormField title={Translations.TreatmentEfficiency}>
                                                    <IdTitleDropdown
                                                        disabled={isReadOnly}
                                                        selectedId={state.treatmentEfficiency.toString()}
                                                        items={treatmentEfficiencyOptions}
                                                        onChange={(value: string) => setState((old) => {
                                                            const treatmentEfficiency = value.toInteger();
                                                            const residualRiskLevel = getResidualRiskLevelColor(old.riskLevel, treatmentEfficiency);
                                                            return {
                                                                ...old,
                                                                treatmentEfficiency,
                                                                residualRiskLevel
                                                            };
                                                        })}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={4}>
                                                <FormField title={Translations.ResidualRisk} fullWidth>
                                                    <RiskListLevelChip riskLevel={state.residualRiskLevel} fullWidth />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={4}>
                                                <FormField title={Translations.Traceability} fullWidth>
                                                    <RiskListTraceabilityChip riskLevel={state.residualRiskLevel} fullWidth />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.TreatmentTargetDate} fullWidth>
                                                    <DatePicker
                                                        disabled={isReadOnly}
                                                        value={state.treatmentTargetDateStr}
                                                        onChange={(treatmentTargetDateStr: string) => setState((old) => ({ ...old, treatmentTargetDateStr }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={6}>
                                                <UserBlocks
                                                    userCode={props.sessionState.code}
                                                    companyId={props.companyId}
                                                    isReadOnly={isReadOnly}
                                                    title={Translations.PersonsInCharge}
                                                    users={state.personsInCharge}
                                                    removeUsersConfirmation={Translations.PersonsInChargeRemoveConfirmation}
                                                    onAddUsers={users => handleAddHandlers(users)}
                                                    onRemoveUsers={userCodes => handleRemoveHandlers(userCodes)}
                                                    onGetUserSelectListItems={handleGetHandlersSelectListItems}
                                                    onApprove={userCode => handleApprove(userCode)}
                                                />
                                            </Grid>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.State} fullWidth>
                                                    {RiskListItem.getRiskTreatmentStateText(handlersNotSpecified && treatmentTargetDateNotSpecified && treatmentsNotSpecified
                                                        ? RiskTreatmentState.NotNeeded
                                                        : (handlersNotSpecified && treatmentTargetDateNotSpecified && !treatmentsNotSpecified
                                                            ? RiskTreatmentState.TreatmentsSpecified
                                                            : (!handlersNotSpecified && state.personsInCharge.length === state.personsInCharge.filter(i => !!i.date).length
                                                                ? RiskTreatmentState.Completed
                                                                : (treatmentTargetDateNotSpecified || state.treatmentTargetDateStr.toUtcDate().getTime() >= Base.getNowDateTicks()
                                                                    ? RiskTreatmentState.InProgress
                                                                    : RiskTreatmentState.Late))))}
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    </DialogSectionCard>
                                    <DialogSectionCard headerTitle={Translations.Approval} height="auto" margin="15px 0 0 0" className="approvalSection">
                                        <Alert severity="info">{String.format(Translations.RiskApprovalInfo, residualRiskLevelThreshold.toString(10))}</Alert>
                                        <GridRow>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.RiskApproval} fullWidth>
                                                    <IdTitleDropdown
                                                        disabled={isReadOnly}
                                                        selectedId={state.approval.toString()}
                                                        items={approvalOptions}
                                                        onChange={(value: string) => setState((old) => ({ ...old, approval: value.toInteger(true, valueNotSelected) }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={3}>
                                                <FormField title={Translations.ApprovalDate} fullWidth>
                                                    <DatePicker
                                                        disabled={isReadOnly}
                                                        value={state.approvalDateStr}
                                                        maxDate={nowDateStr}
                                                        onChange={(approvalDateStr: string) => setState((old) => ({ ...old, approvalDateStr }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                            <Grid item sm={6}>
                                                <FormField title={Translations.ApprovalAuthority} fullWidth>
                                                    <FormText
                                                        required={state.residualRiskLevel > 10}
                                                        disabled={isReadOnly}
                                                        tooltip={Translations.ApprovalAuthority}
                                                        value={state.approvalAuthority}
                                                        maxLength={100}
                                                        onChange={(approvalAuthority) => setState((old) => ({ ...old, approvalAuthority }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <FormField title={Translations.ApprovalJustification} fullWidth>
                                                    <FormText
                                                        disabled={isReadOnly}
                                                        maxLength={1000}
                                                        placeholder={Translations.ApprovalJustificationPlaceholder}
                                                        tooltip={Translations.ApprovalJustification}
                                                        value={state.approvalJustification}
                                                        multiline
                                                        rows={6}
                                                        onChange={(approvalJustification) => setState((old) => ({ ...old, approvalJustification }))}
                                                    />
                                                </FormField>
                                            </Grid>
                                        </GridRow>
                                    </DialogSectionCard>
                                </Box>
                            </Grid>
                            <Grid item sm={3} height="100%">
                                <DialogSectionCard
                                    headerTitle={Translations.Summary}
                                    height="100%"
                                    infoTitle={Translations.RiskMatrix}
                                    infoDocumentIsHtml
                                    getInfoDocument={() => riskService.downloadInfoDocument(optionCodeRiskMatrixInfoDocument)}
                                >
                                    <Box sx={{ overflowY: "auto" }}>
                                        <FormField title={<RiskProcessTitle process={assessmentProcess} title={Translations.Assessment} containerDiv={containerDiv} className="assessmentSection" />} margin={0}>
                                            <Box sx={{ paddingLeft: "24px" }}>
                                                <Box marginBottom="8px" display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>{Translations.RiskLevel}</Box>
                                                    <Box display="flex" alignItems="center">
                                                        <RiskListLevelChip riskLevel={state.riskLevel} />
                                                        {(state.previousRiskLevel
                                                            ? <Box marginLeft="2px">
                                                                <AppTrendingIcon
                                                                    prevValue={state.previousRiskLevel}
                                                                    value={state.riskLevel}
                                                                />
                                                            </Box>
                                                            : null
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box display="flex" alignItems="center" justifyContent="space-between" paddingRight={state.previousRiskLevel ? "26px" : undefined}>
                                                    <Box>{Translations.Traceability}</Box>
                                                    <RiskListTraceabilityChip riskLevel={state.riskLevel} />
                                                </Box>
                                            </Box>
                                        </FormField>
                                        <FormField title={<RiskProcessTitle process={riskTreatmentsProcess} title={Translations.RiskTreatments} containerDiv={containerDiv} className="riskTreatmentsSection" />}>
                                            <Box sx={{ paddingLeft: "24px" }}>
                                                <Box marginBottom="8px" display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box>{Translations.ResidualRisk}</Box>
                                                    <Box display="flex" alignItems="center">
                                                        <RiskListLevelChip riskLevel={state.residualRiskLevel} />
                                                        {(state.previousResidualRiskLevel
                                                            ? <Box marginLeft="2px">
                                                                <AppTrendingIcon
                                                                    prevValue={state.previousResidualRiskLevel}
                                                                    value={state.residualRiskLevel}
                                                                />
                                                            </Box>
                                                            : null
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box display="flex" alignItems="center" justifyContent="space-between" paddingRight={state.previousResidualRiskLevel ? "26px" : undefined}>
                                                    <Box>{Translations.Traceability}</Box>
                                                    <RiskListTraceabilityChip riskLevel={state.residualRiskLevel} />
                                                </Box>
                                            </Box>
                                        </FormField>
                                        {(state.residualRiskLevel >= residualRiskLevelThreshold || state.approval > ApprovalType.NotApproved) &&
                                            <FormField title={<RiskProcessTitle process={approvalProcess} title={Translations.Approval} containerDiv={containerDiv} className="approvalSection" />}>
                                                <Box sx={{ paddingLeft: "24px" }}>
                                                    <Box marginBottom="8px" display="flex" alignItems="center" justifyContent="space-between">
                                                        <Box>{Translations.RiskApproval}</Box>
                                                        <RiskListApprovalChip approval={state.approval < 0 ? null : state.approval} />
                                                    </Box>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                                        <Box>{Translations.ApprovalAuthority}</Box>
                                                        <Box>{state.approvalAuthority}</Box>
                                                    </Box>
                                                </Box>
                                            </FormField>
                                        }
                                    </Box>
                                </DialogSectionCard>
                            </Grid>
                        </GridRow>
                    </Box>
                </>
            }
            actions={
                <Box display="flex" justifyContent="space-between" width="100%">
                    <DialogItemsNav
                        hasPrevious={props.hasPrevious}
                        hasNext={props.hasNext}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        createUser={!state.isNew ? state.riskAssessment.createUser : null}
                        modifyUser={!state.isNew ? state.riskAssessment.modifyUser : null}
                        lockedUser={state.canEdit ? state.lockedUser : null}
                        isLockedToOtherUser={!state.isNew && state.canEdit && state.isLockedToOtherUser}
                        onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? handleUnlockAndLockToMe : null}
                    />
                    <DialogButtons
                        isNew={state.isNew}
                        removeDisabled={state.isLockedToOtherUser || !state.canEdit}
                        saveDisabled={state.isLockedToOtherUser}
                        onCancel={handleCancel}
                        //onPrint={!state.isNew ? handlePrint : null}
                        onRemove={canDelete
                            ? handleRemove
                            : null}
                        onSave={!isReadOnly ? () => handleSave(false) : null}
                    />
                </Box>
            }
            onClose={handleCancel}/>
    );
};